import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ArrowRight, Download } from 'lucide-react';
import BeforeAfter from './components/BeforeAfter';
import CustomPin from './components/CustomPin';
import ProcessCarousel from './components/ProcessCarousel';

const HoverButton = ({ defaultContent, hoverContent, className }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered ? hoverContent : defaultContent}
    </button>
  );
};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-hue-bg font-sans text-hue-font selection:bg-hue-purple selection:text-white pb-12">
      {/* NAVBAR - Static Top */}
      <nav className="w-full bg-white border-b border-slate-100 px-4 sm:px-6 lg:px-8 py-4 mb-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Hue Logo" className="h-10 w-auto" />
            <div className="text-3xl font-black text-hue-purple tracking-tight">Hue</div>
          </div>

          <div className="hidden md:flex items-center space-x-6">
            <Link to="/about" className="font-bold text-slate-500 hover:text-hue-purple transition">Nosotros</Link>
            <Link to="/vecinos" className="font-bold text-slate-500 hover:text-hue-purple transition">Vecinos</Link>
            <Link to="/support" className="font-bold text-slate-500 hover:text-hue-purple transition">Ayuda</Link>
            <HoverButton
              className="bg-hue-purple text-white px-6 py-2.5 rounded-full hover:bg-purple-700 transition font-bold shadow-lg shadow-purple-200 min-w-[160px]"
              defaultContent="Descargar App"
              hoverContent="Próximamente"
            />
          </div>

          <button className="md:hidden text-slate-600" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* MAIN GRID LAYOUT */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">

        {/* ROW 1: Hero Cards */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Card: Purple Hero */}
          <div className="bg-hue-purple rounded-[2.5rem] p-10 md:p-16 flex flex-col justify-center text-white shadow-xl min-h-[500px]">
            <h1 className="text-6xl md:text-7xl font-black mb-6 leading-[0.9] tracking-tight">
              HUE
            </h1>
            <h3 className="text-3xl md:text-4xl font-bold mb-6 opacity-90">
              Tu ciudad, Tu Hogar
            </h3>
            <h5 className="text-xl md:text-2xl font-medium opacity-80 leading-relaxed max-w-md">
              Cuida donde vives e informa a tu municipio en tiempo real.
            </h5>
          </div>

          {/* Right Card: City Map Mockup */}
          <div className="bg-hue-light-blue rounded-[2.5rem] overflow-hidden shadow-xl relative min-h-[500px] group">
            <img
              src="/foto_stgo_aerea.jpg"
              alt="Santiago de Chile Aérea"
              className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-hue-light-blue/40 to-transparent"></div>

            {/* Floating Pins - Positioned on city streets */}
            <div className="absolute top-1/2 left-1/3 animate-bounce duration-[2000ms]">
              <CustomPin
                pinColor="#3D280D"
                iconColor="#c2d7f2"
                iconPath={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM8 9h8v10H8zm7.5-5l-1-1h-5l-1 1H5v2h14V4z" /></svg>}
              />
            </div>
            <div className="absolute bottom-1/4 right-1/4 animate-bounce duration-[2500ms]">
              <CustomPin
                pinColor="#FF8000"
                iconColor="#007FFF"
                iconPath={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2m-3-3h6c.55 0 1-.45 1-1s-.45-1-1-1H9c-.55 0-1 .45-1 1s.45 1 1 1m3-17C7.86 2 4.5 5.36 4.5 9.5c0 3.82 2.66 5.86 3.77 6.5h7.46c1.11-.64 3.77-2.68 3.77-6.5C19.5 5.36 16.14 2 12 2" /></svg>}
              />
            </div>
          </div>
        </div>

        {/* ROW 2: Process Carousel */}
        <div className="w-full h-[400px]">
          <ProcessCarousel />
        </div>

        {/* ROW 3: Community & Before/After */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Card: Olive Community */}
          <div className="bg-hue-olive rounded-[2.5rem] p-10 md:p-14 flex flex-col justify-center shadow-xl min-h-[400px]">
            <h2 className="text-4xl md:text-5xl font-black text-hue-dark-text mb-6 leading-tight">
              El cambio lo hacemos nosotros
            </h2>
            <h4 className="text-xl md:text-2xl font-medium text-hue-dark-text leading-relaxed">
              Como comunidad depende de nosotros tener un lugar digno para vivir y convivir día a día.
            </h4>
          </div>

          {/* Right Card: Before/After Slider */}
          <div className="bg-white rounded-[2.5rem] p-4 shadow-xl border border-slate-100 min-h-[400px] flex items-center">
            <BeforeAfter
              beforeImage="/lynch-basura.jpg"
              afterImage="/lynch-limpio.jpg"
              alt="Limpieza de calle"
            />
          </div>
        </div>

        {/* ROW 4: Dashboard Info */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Card: Light Blue Info */}
          <div className="bg-hue-light-blue rounded-[2.5rem] p-10 md:p-14 flex flex-col justify-center shadow-xl min-h-[400px]">
            <h2 className="text-4xl md:text-5xl font-black text-hue-black mb-6 leading-tight">
              Dashboard Municipal
            </h2>
            <h4 className="text-xl md:text-2xl font-medium text-hue-black leading-relaxed">
              Monitoreo en tiempo real del estado de los reportes ciudadanos.
            </h4>
          </div>

          {/* Right Card: Dashboard Image */}
          <div className="bg-white rounded-[2.5rem] overflow-hidden shadow-xl border border-slate-100 min-h-[400px] relative">
            <img
              src="/hero-dashboard.png"
              alt="Dashboard Hue"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>

        {/* ROW 5: CTA */}
        <div className="bg-hue-purple rounded-[2.5rem] p-12 md:p-20 text-center shadow-xl">
          <img src="/logo.png" alt="Hue Logo" className="h-32 w-auto mx-auto mb-8 brightness-0 invert" />
          <h1 className="text-4xl md:text-6xl font-black text-white mb-10">
            ¿Listo para cuidar tu comuna?
          </h1>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <HoverButton
              className="bg-black text-white px-8 py-4 rounded-xl hover:bg-slate-900 transition flex items-center justify-center gap-3 shadow-lg min-w-[200px]"
              defaultContent={
                <>
                  <Download size={24} />
                  <div className="text-left">
                    <div className="text-xs font-medium">Disponible en</div>
                    <div className="text-xl font-bold">App Store</div>
                  </div>
                </>
              }
              hoverContent={<span className="text-xl font-bold">Próximamente</span>}
            />
            <HoverButton
              className="bg-black text-white px-8 py-4 rounded-xl hover:bg-slate-900 transition flex items-center justify-center gap-3 shadow-lg min-w-[200px]"
              defaultContent={
                <>
                  <Download size={24} />
                  <div className="text-left">
                    <div className="text-xs font-medium">DISPONIBLE EN</div>
                    <div className="text-xl font-bold">Google Play</div>
                  </div>
                </>
              }
              hoverContent={<span className="text-xl font-bold">Próximamente</span>}
            />
          </div>
        </div>

      </main>

      {/* FOOTER */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 pt-10 border-t border-slate-200">
        <div className="grid md:grid-cols-4 gap-10 mb-10">
          <div className="col-span-1">
            <h3 className="text-2xl font-black text-hue-font mb-2">Hue</h3>
            <h5 className="text-slate-500 font-medium">Vecinos ayudando a su comunidad</h5>
          </div>
          {/* Column 2 */}
          <div>
            <h4 className="font-bold text-hue-font mb-4">Comunidad</h4>
            <ul className="space-y-2 text-slate-500 text-sm">
              <li><Link to="/vecinos" className="hover:text-hue-purple">Vecinos</Link></li>
              <li><Link to="/blog" className="hover:text-hue-purple">Blog</Link></li>
              <li><a href="#" className="hover:text-hue-purple">Foro</a></li>
            </ul>
          </div>

          {/* Column 3 */}
          <div>
            <h4 className="font-bold text-hue-font mb-4">Soporte</h4>
            <ul className="space-y-2 text-slate-500 text-sm">
              <li><Link to="/support" className="hover:text-hue-purple">Centro de ayuda</Link></li>
              <li><Link to="/terms" className="hover:text-hue-purple">Términos de uso</Link></li>
              <li><Link to="/privacy" className="hover:text-hue-purple">Privacidad</Link></li>
            </ul>
          </div>

          {/* Column 4 */}
          <div>
            <h4 className="font-bold text-hue-font mb-4">Hue</h4>
            <ul className="space-y-2 text-slate-500 text-sm">
              <li><Link to="/about" className="hover:text-hue-purple">Sobre nosotros</Link></li>
              <li><Link to="/press" className="hover:text-hue-purple">Prensa</Link></li>
              <li><Link to="/contact" className="hover:text-hue-purple">Contacto</Link></li>
            </ul>
          </div>
        </div>
        <div className="text-center text-slate-400 text-sm py-6 border-t border-slate-100">
          &copy; 2025 Hue. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
